import beerIcon from "../assets/page_logo.png";
import checkIcon from "../assets/check.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { authApi } from "../services/api";
import { toast } from "react-toastify";

// Same icon set as createPassword.js so the two password screens read identically.
const EYE_FILL = "https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-fill.svg";
const EYE_SLASH = "https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-slash-fill.svg";

/**
 * Finish a signup that was abandoned at the OTP step.
 *
 * Deliberately stores nothing. /confirm/new needs the server-generated `temp_password`
 * echoed back, which would mean keeping a live credential in localStorage purely to survive
 * a page reload. /api/auth/verify-email takes the code alone and returns a short-lived,
 * single-use ticket, which lives in React state for the seconds it takes the user to choose
 * a password. Nothing sensitive touches disk, so verification also works from a device that
 * never saw the signup.
 *
 * Spec: docs/spec/partner-onboarding.md (AC-PARTNER-E10, E11)
 */

const RESEND_COOLDOWN_SECONDS = 60;

const STEPS = { EMAIL: "email", CODE: "code", PASSWORD: "password" };

function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const cooldownRef = useRef(null);

  // Prefill from the ?email= adminLogin passes when it detects an unconfirmed account.
  useEffect(() => {
    const queryEmail = searchParams.get("email");
    if (queryEmail) setEmail(queryEmail);
  }, [searchParams]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    cooldownRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(cooldownRef.current);
  }, [cooldown]);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  // Mirrors createPassword.js:29-34. The special character must come from `!@#^*_?{}-`;
  // `$` and `%` are not in that set.
  const criteria = {
    upper: /[A-Z]/.test(newPassword),
    number: /\d/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    special: /[!@#^*_?{}-]/.test(newPassword),
    length: newPassword.length >= 8,
  };
  const allSatisfied = Object.values(criteria).every(Boolean);
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;

  const rules = [
    { label: "At least one uppercase letter", satisfied: criteria.upper },
    { label: "At least one numeral (0-9)", satisfied: criteria.number },
    { label: "At least one lowercase letter", satisfied: criteria.lower },
    { label: "At least one special symbol (!@#^*_?{}-)", satisfied: criteria.special },
    { label: "Minimum of 8 characters", satisfied: criteria.length },
  ];

  const showError = (message) =>
    toast.error(message, { position: "top-right", autoClose: 4000 });

  const handleSendCode = async () => {
    if (!isValidEmail(email)) {
      showError("Please enter a valid email address.");
      return;
    }

    setBusy(true);
    try {
      const data = await authApi.resendSignupOTP({ email: email.trim().toLowerCase() });

      // This API answers with HTTP 200 and an { error } body, so success has to be checked
      // for explicitly rather than inferred from the status code.
      if (data.error) {
        showError(data.error);
        return;
      }

      setStep(STEPS.CODE);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success("Verification code sent to your email.", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      showError(err.error || err.message || "Failed to send verification code.");
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async () => {
    if (code.trim().length !== 6) {
      showError("Please enter the 6-digit code from your email.");
      return;
    }

    setBusy(true);
    try {
      const data = await authApi.verifyEmail({
        email: email.trim().toLowerCase(),
        otp: code.trim(),
      });

      if (!data.ticket) {
        showError(data.error || "Invalid or expired code. Please try again.");
        return;
      }

      setTicket(data.ticket);
      setStep(STEPS.PASSWORD);
      toast.success("Email verified. Now choose a password.", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      showError(err.error || err.message || "Invalid or expired code.");
    } finally {
      setBusy(false);
    }
  };

  const handleSetPassword = async () => {
    setBusy(true);
    try {
      const data = await authApi.changePassword({
        ticket,
        new_password: newPassword,
      });

      // password_reset_handler is the one auth call that uses the ok_resp/err_resp envelope
      // (utils/helpers.py), so a failure is { ok:false, error:{ code, detail } } — error is an
      // OBJECT, not the bare string the other endpoints return. Reading data.error directly
      // toasts "[object Object]"; the human-readable reason is error.detail. The most likely
      // trigger is an expired ticket (10-minute TTL) after a slow password step.
      if (data.error) {
        const message =
          typeof data.error === "string"
            ? data.error
            : data.error.detail || data.error.code || "Failed to set password.";
        showError(message);
        return;
      }

      toast.success("Account verified. You can now log in.", {
        position: "top-right",
        autoClose: 2500,
      });
      setTimeout(
        () =>
          navigate(`/admin-login?email=${encodeURIComponent(email.trim().toLowerCase())}`),
        600,
      );
    } catch (err) {
      const fromError =
        typeof err.error === "object" ? err.error?.detail || err.error?.code : err.error;
      showError(
        fromError || err.details?.detail || err.message || "Failed to set password.",
      );
    } finally {
      setBusy(false);
    }
  };

  const primaryButton = (enabled) => ({
    width: "100%",
    padding: "14px",
    borderRadius: "30px",
    border: "none",
    cursor: enabled ? "pointer" : "not-allowed",
    background: enabled ? "#DE810E" : "rgba(222, 129, 14, 0.4)",
    color: "white",
    fontWeight: "bold",
    fontSize: "1rem",
    opacity: enabled ? 1 : 0.6,
    transition: "all 0.3s ease",
  });

  // Type scale and control metrics are taken from adminLogin/adminSignup so the three
  // screens read as one flow: 14px bold labels, 12px/8px inputs, 1.8rem #1E344D headings.
  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    marginBottom: "16px",
    marginTop: "5px",
    boxSizing: "border-box",
  };

  const labelStyle = { fontSize: "14px", fontWeight: "bold", color: "black" };

  // Password fields mirror createPassword.js: a taller control with room on the right for the
  // orange "valid" badge and the show/hide eye, so the two screens feel like one component.
  const passwordFieldLabel = { ...labelStyle, display: "block", marginBottom: "5px" };
  const passwordInputStyle = {
    width: "100%",
    height: "52px",
    padding: "12px 68px 12px 12px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
    fontSize: "16px",
  };
  const eyeStyle = {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    cursor: "pointer",
  };
  const validBadgeStyle = {
    position: "absolute",
    right: "40px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
  };

  const canSend = isValidEmail(email) && !busy && cooldown === 0;
  const canVerify = code.trim().length === 6 && !busy;
  const canSave = allSatisfied && passwordsMatch && !busy;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        background: "#F9F4EA",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "432px",
          background: "white",
          borderRadius: "12px",
          padding: "30px",
          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
        }}
      >
        <img
          src={beerIcon}
          alt="myHoom"
          style={{
            height: "88px",
            display: "block",
            margin: "0 auto 20px",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        />

        <h1
          style={{
            textAlign: "center",
            fontSize: "1.8rem",
            fontWeight: "bold",
            color: "#1E344D",
            marginBottom: "8px",
          }}
        >
          {step === STEPS.PASSWORD ? "Create New Password" : "Verify your email"}
        </h1>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            fontSize: "14px",
            lineHeight: 1.5,
            marginBottom: "24px",
          }}
        >
          {step === STEPS.EMAIL &&
            "Didn't finish verifying when you signed up? Enter your email to get a new code."}
          {step === STEPS.CODE && `We sent a 6-digit code to ${email}.`}
          {step === STEPS.PASSWORD && "Your email is verified. Set a password to finish."}
        </p>

        {step !== STEPS.PASSWORD && (
          <>
            <label htmlFor="verify-email" style={labelStyle}>
              Email address
            </label>
            <input
              id="verify-email"
              type="email"
              value={email}
              placeholder="operator@gmail.com"
              disabled={step === STEPS.CODE}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />

            <button
              type="button"
              onClick={handleSendCode}
              disabled={!canSend}
              style={primaryButton(canSend)}
            >
              {busy && step === STEPS.EMAIL
                ? "Sending..."
                : cooldown > 0
                  ? `Resend code in ${cooldown}s`
                  : step === STEPS.CODE
                    ? "Resend code"
                    : "Send code"}
            </button>
          </>
        )}

        {step === STEPS.CODE && (
          <div style={{ marginTop: "28px" }}>
            <label htmlFor="verify-code" style={labelStyle}>
              6-digit code
            </label>
            <input
              id="verify-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              placeholder="Enter 6-digit code"
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              style={{
                ...inputStyle,
                letterSpacing: "8px",
                textAlign: "center",
                fontSize: "1.3rem",
              }}
            />
            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={!canVerify}
              style={primaryButton(canVerify)}
            >
              {busy ? "Verifying..." : "Verify email"}
            </button>
          </div>
        )}

        {step === STEPS.PASSWORD && (
          <>
            {/* New password */}
            <div style={{ position: "relative", marginBottom: "24px" }}>
              <label htmlFor="new-password" style={passwordFieldLabel}>
                New password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  placeholder="Enter new password"
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={passwordInputStyle}
                />
                {allSatisfied && (
                  <span style={validBadgeStyle}>
                    <img
                      src={checkIcon}
                      alt="valid"
                      style={{ width: "18px", height: "18px" }}
                    />
                  </span>
                )}
                <span
                  data-testid="toggle-new-password"
                  onClick={() => setShowPassword(!showPassword)}
                  style={eyeStyle}
                >
                  <img
                    src={showPassword ? EYE_FILL : EYE_SLASH}
                    alt={showPassword ? "Hide password" : "Show password"}
                    style={{ width: "20px", height: "20px" }}
                  />
                </span>
              </div>
            </div>

            {/* Requirement checklist */}
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px" }}>
              {rules.map((rule) => (
                <li
                  key={rule.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                    fontSize: "14px",
                    color: rule.satisfied ? "#1E344D" : "rgba(30, 52, 77, 0.3)",
                    transition: "color 0.3s ease",
                  }}
                >
                  <img
                    src={checkIcon}
                    alt=""
                    style={{
                      width: "14px",
                      height: "14px",
                      marginRight: "8px",
                      opacity: rule.satisfied ? 1 : 0.3,
                      transition: "opacity 0.3s ease",
                    }}
                  />
                  {rule.label}
                </li>
              ))}
            </ul>

            {/* Confirm password */}
            <div style={{ position: "relative", marginBottom: "24px" }}>
              <label htmlFor="confirm-password" style={passwordFieldLabel}>
                Confirm password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  placeholder="Re-enter new password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{
                    ...passwordInputStyle,
                    border:
                      confirmPassword.length === 0
                        ? "1px solid #ccc"
                        : passwordsMatch
                          ? "1px solid green"
                          : "1px solid red",
                  }}
                />
                {passwordsMatch && (
                  <span style={validBadgeStyle}>
                    <img
                      src={checkIcon}
                      alt="valid"
                      style={{ width: "18px", height: "18px" }}
                    />
                  </span>
                )}
                <span
                  data-testid="toggle-confirm-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={eyeStyle}
                >
                  <img
                    src={showConfirmPassword ? EYE_FILL : EYE_SLASH}
                    alt={showConfirmPassword ? "Hide password" : "Show password"}
                    style={{ width: "20px", height: "20px" }}
                  />
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSetPassword}
              disabled={!canSave}
              style={primaryButton(canSave)}
            >
              {busy ? "Saving..." : "Save password"}
            </button>
          </>
        )}

        <p
          style={{
            textAlign: "center",
            marginTop: "24px",
            fontSize: "16px",
            color: "black",
          }}
        >
          Already verified?{" "}
          <span
            onClick={() => navigate("/admin-login")}
            style={{ color: "#DE810E", fontWeight: "bold", cursor: "pointer" }}
          >
            Log in
          </span>
        </p>
      </div>
    </div>
  );
}

export default VerifyEmail;
